/**
 * PageLinkPicker — Suggestion that fires on `[[` and opens a cmdk palette
 * over the workspace's page tree. Picking a page inserts a `pageLink`
 * node with the resolved `{ pageId, slug, title, icon }`.
 *
 * We use `char: '[['` and `allowSpaces: true` (titles can have spaces).
 * The trigger characters are consumed before insert so the user never
 * sees `[[` in the document.
 */

import { Extension } from '@tiptap/core'
import { Suggestion } from '@tiptap/suggestion'
import { ReactRenderer } from '@tiptap/react'
import { PluginKey } from '@tiptap/pm/state'
import { mountSuggestion, unmountSuggestion } from './suggestionRenderer'
import { PageLinkPicker, type PageLinkPickerRef } from '@/components/notebook/PageLinkPicker'
import type { Page } from '@friday/shared/realtime'

/**
 * Three suggestion-using extensions share the editor (slash, mention,
 * page-link). Each `Suggestion({...})` defaults to the shared
 * `SuggestionPluginKey`; without distinct keys ProseMirror throws
 * "Adding different instances of a keyed plugin (suggestion$)".
 */
const PageLinkPluginKey = new PluginKey('page-link-suggestion')

export interface PageLinkSuggestionOptions {
  wsId: string
  /**
   * Returns the latest page tree. Called every time the suggestion
   * query fires, so the picker always shows the freshest results
   * without requiring the extension to be re-created.
   */
  getPageTree: () => Page[]
}

export const PageLinkSuggestion = Extension.create<PageLinkSuggestionOptions>({
  name: 'pageLinkSuggestion',
  addOptions() {
    return { wsId: '', getPageTree: () => [] }
  },
  addProseMirrorPlugins() {
    return [
      Suggestion({
        editor: this.editor,
        pluginKey: PageLinkPluginKey,
        char: '[[',
        allowSpaces: true,
        startOfLine: false,
        items: ({ query }) => {
          const q = query.toLowerCase()
          return this.options
            .getPageTree()
            .filter((p) => !p.archivedAt)
            .filter(
              (p) =>
                p.title.toLowerCase().includes(q) ||
                p.slug.toLowerCase().includes(q),
            )
            .slice(0, 8)
        },
        command: ({ editor, range, props }) => {
          const page = props as Page
          editor
            .chain()
            .focus()
            .deleteRange(range)
            .insertContent([
              {
                type: 'pageLink',
                attrs: {
                  pageId: page.id,
                  slug: page.slug,
                  title: page.title,
                  icon: page.icon,
                },
              },
              { type: 'text', text: ' ' },
            ])
            .run()
        },
        render: () => {
          let component: ReactRenderer<PageLinkPickerRef> | null = null
          return {
            onStart: (props) => {
              component = new ReactRenderer(PageLinkPicker, {
                props,
                editor: props.editor,
              })
              mountSuggestion(component)
            },
            onUpdate: (props) => {
              component?.updateProps(props)
            },
            onKeyDown: (props) => {
              if (props.event.key === 'Escape') {
                return true
              }
              return component?.ref?.onKeyDown(props) ?? false
            },
            onExit: () => {
              unmountSuggestion(component)
              component = null
            },
          }
        },
      }),
    ]
  },
})
